// CameraRayOutside
// Features:
// - Casts a ray from camera through mouse pointer in the outside scene
// - Highlights cabin exterior on hover (dim light + pointer cursor)
// - Transitions phase to `enteringCabin` on click

import { useEffect, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { useRapier } from '@react-three/rapier'
import * as THREE from 'three'

import { useStore } from '../store/useStore'
import { useActions } from '../store/useActions'
import { usePhase, PHASES } from '../store/usePhase'

const CABIN_OUTSIDE_NAME = 'cabin_outside'
const DEFAULT_MAX_DISTANCE = 20

// Cursor Helpers
const setPointerCursor = () => {
    const target = document.getElementById('root') || document.body
    target.style.cursor = 'pointer'
}

const resetPointerCursor = () => {
    const target = document.getElementById('root') || document.body
    target.style.removeProperty('cursor')
}

export default function CameraRayOutside({
    maxDistance = DEFAULT_MAX_DISTANCE,
    solid = true,
}) {
    const { camera } = useThree()
    const { rapier, world } = useRapier()

    const hoveredRef = useRef(false)
    const rayDir = useRef(new THREE.Vector3())
    const ndc = useRef(new THREE.Vector3())

    const dimOutside = useActions((state) => state.dimOutside)
    const normalizeOutside = useActions((state) => state.normalizeOutside)

    useFrame(({ pointer }) => {
        if (!camera || !world) return

        // 1. Setup Ray from Mouse Position
        ndc.current.set(pointer.x, pointer.y, 0.5)
        ndc.current.unproject(camera)
        rayDir.current.copy(ndc.current).sub(camera.position).normalize()

        const ray = new rapier.Ray(camera.position, rayDir.current)

        // 2. Cast Ray (Only against cabin outside)
        const hit = world.castRay(
            ray,
            maxDistance,
            solid,
            undefined,
            undefined,
            undefined,
            (collider) => {
                const parent = collider.parent && collider.parent()
                const name = parent?.userData?.name
                return name === CABIN_OUTSIDE_NAME
            }
        )

        // 3. Handle Hover State
        if (hit?.collider) {
            if (!hoveredRef.current) {
                hoveredRef.current = true
                dimOutside()
                setPointerCursor()
            }
            useStore.getState().setCurrentOutsideHitName(CABIN_OUTSIDE_NAME)
        } else {
            if (hoveredRef.current) {
                hoveredRef.current = false
                normalizeOutside()
                resetPointerCursor()
            }
            useStore.getState().setCurrentOutsideHitName(null)
        }
    })

    // Interaction: Click to Enter Cabin
    useEffect(() => {
        const onMouseDown = (e) => {
            if (e.button !== 0) return

            const hitName = useStore.getState().currentOutsideHitName
            if (hitName === CABIN_OUTSIDE_NAME) {
                resetPointerCursor()

                // Immediate state updates
                useActions.getState().normalizeOutside()
                usePhase.getState().setPhase(PHASES.enteringCabin)
            }
        }

        window.addEventListener('mousedown', onMouseDown)
        return () => window.removeEventListener('mousedown', onMouseDown)
    }, [])

    return null
}
